<script>
	import { onMount, onDestroy } from "svelte";
	import { getApi, refreshPocUsers } from "../../service/api";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import {
		mqttDevices,
		connectSSE,
		disconnectSSE,
		loadBrokerStatus,
	} from "../../service/mqtt";
	import { PSQI_BY_BULB_SN, psqiColor } from "./components/PsqiData.js";
	import {
		parseSleepStage,
		parseSamsungStages,
		parseHeartRate,
		parseLocalTimeMs,
	} from "./components/DataParser.js";
	import SleepStageChart from "./components/SleepStageChart.svelte";
	import HeartRateChart from "./components/HeartRateChart.svelte";
	import StageRatioChart from "./components/StageRatioChart.svelte";
	import ComparisonCard from "./components/ComparisonCard.svelte";
	import SessionInfoCard from "./components/SessionInfoCard.svelte";

	let excludeInternal = true;
	let dateFrom = "";
	let dateTo = "";
	let loading = false;

	let kpiData = null;
	let heatmapData = null;
	let usersData = [];
	let usersCachedAt = "";
	let usersRefreshing = false;
	let weeklyData = null;
	let heatmapMode = "se"; // 'se' | 'care'
	let heatmapTimeMode = "date"; // 'date' | 'week'
	let weeklyHeatmapData = null;

	// 세션 상세 모달
	let modalOpen = false;
	let modalLoading = false;
	let modalSessions = []; // [{id, timeStart, timeEnd}]
	let modalActiveTab = 0;
	let modalCompareCache = {}; // id -> parsed compare data
	let modalLabel = ""; // "user@email.com / 2024-03-01"

	// 모달 내 현재 탭의 파싱된 데이터
	let modalOurStage = [];
	let modalSamsungStage = [];
	let modalOurHR = [];
	let modalSamsungHR = [];
	let modalOurRatios = null;
	let modalSamsungRatios = null;
	let modalMergedSamsung = null;
	let modalSleepwaveData = null;
	let modalSamsungCount = 0;

	$: dates = heatmapData?.dates || [];
	$: heatmapUsers = heatmapData?.users || [];
	$: onlineNow = usersData.filter(
		(u) => $mqttDevices["tasmota_" + u.userId]?.online === "true",
	).length;
	// userId → firstBulbSerial 매핑 (PSQI 히트맵 레이블용)
	$: bulbSerialById = Object.fromEntries(
		usersData.map((u) => [u.userId, u.firstBulbSerial]),
	);

	const POC_TOTAL = 43;

	function getDefaultDates() {
		const now = new Date();
		const fmt = (d) =>
			`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
		const fromDate = new Date(now);
		fromDate.setDate(fromDate.getDate() - 29);
		return { from: fmt(fromDate), to: fmt(now) };
	}

	function secsToHMS(s) {
		if (!s && s !== 0) return "-";
		const totalSecs = Math.round(s);
		const h = Math.floor(totalSecs / 3600);
		const m = Math.floor((totalSecs % 3600) / 60);
		return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
	}

	const CELL_EMPTY = "background:#fff;border:1px solid #cbd5e1;cursor:default";

	function cellStyle(se, date, firstCareDate) {
		if (date === firstCareDate) return "background:#f472b6";
		if (se === null || se === undefined) return CELL_EMPTY;
		const opacity = Math.min(Math.max(se / 100, 0.2), 1).toFixed(3);
		return `background:rgba(99,102,241,${opacity})`;
	}

	function cellTitle(se, date, firstCareDate) {
		const prefix = date === firstCareDate ? "🌱 케어 시작일 | " : "";
		const speStr =
			se !== null && se !== undefined
				? `SPE: ${se.toFixed(1)}%`
				: "케어 없음";
		return `${prefix}${date} / ${speStr}`;
	}

	function careSecsStyle(careSecs, date, firstCareDate) {
		if (date === firstCareDate) return "background:#f472b6";
		if (careSecs === null || careSecs === undefined) return CELL_EMPTY;
		if (careSecs === 0) return "background:#fef3c7";
		const ratio = Math.min(careSecs / 28800, 1);
		const opacity = (0.15 + ratio * 0.85).toFixed(3);
		return `background:rgba(16,185,129,${opacity})`;
	}

	function careSecsTitle(careSecs, date, firstCareDate) {
		const prefix = date === firstCareDate ? "🌱 케어 시작일 | " : "";
		if (careSecs === null || careSecs === undefined)
			return `${prefix}${date} / 케어 없음`;
		const h = Math.floor(careSecs / 3600);
		const m = Math.floor((careSecs % 3600) / 60);
		return `${prefix}${date} / 케어시간: ${h}h ${m}m`;
	}

	function seColor(se) {
		if (!se) return "#94a3b8";
		if (se >= 85) return "#6366f1";
		if (se >= 70) return "#818cf8";
		return "#a78bfa";
	}

	function getPsqi(firstBulbSerial) {
		if (!firstBulbSerial) return null;
		return PSQI_BY_BULB_SN[firstBulbSerial.toUpperCase()] ?? null;
	}

	// 주차별 SPE 셀 스타일
	function weekCellStyle(spe) {
		if (spe === null || spe === undefined) return CELL_EMPTY;
		const opacity = Math.min(Math.max(spe / 100, 0.2), 1).toFixed(3);
		return `background:rgba(99,102,241,${opacity})`;
	}

	function weekCellTitle(spe, dayNum, firstCareDate) {
		const actualDate = firstCareDate
			? addDaysToDate(firstCareDate, dayNum - 1)
			: "";
		const dateStr = actualDate ? ` (${actualDate})` : "";
		if (spe === null || spe === undefined)
			return `${dayNum}일차${dateStr} / 데이터 없음`;
		return `${dayNum}일차${dateStr} (${Math.ceil(dayNum / 7)}주차) / SPE: ${spe.toFixed(1)}%`;
	}

	// 주차별 케어시간 셀 스타일
	function weekCareStyle(careSecs) {
		if (careSecs === null || careSecs === undefined) return CELL_EMPTY;
		if (careSecs === 0) return "background:#fef3c7";
		const ratio = Math.min(careSecs / 28800, 1);
		const opacity = (0.15 + ratio * 0.85).toFixed(3);
		return `background:rgba(16,185,129,${opacity})`;
	}

	function weekCareTitle(careSecs, dayNum, firstCareDate) {
		const actualDate = firstCareDate
			? addDaysToDate(firstCareDate, dayNum - 1)
			: "";
		const dateStr = actualDate ? ` (${actualDate})` : "";
		if (careSecs === null || careSecs === undefined)
			return `${dayNum}일차${dateStr} / 데이터 없음`;
		const h = Math.floor(careSecs / 3600);
		const m = Math.floor((careSecs % 3600) / 60);
		return `${dayNum}일차${dateStr} (${Math.ceil(dayNum / 7)}주차) / 케어시간: ${h}h ${m}m`;
	}

	// dayOffset → 실제 날짜 (YYYY-MM-DD)
	function addDaysToDate(dateStr, days) {
		const base = dateStr ? dateStr.slice(0, 10) : "";
		if (!base || base.length < 10) return null;
		const d = new Date(base + "T00:00:00");
		if (isNaN(d.getTime())) return null;
		d.setDate(d.getDate() + days);
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		const dd = String(d.getDate()).padStart(2, "0");
		return `${yyyy}-${mm}-${dd}`;
	}

	async function openSessionModal(userId, date, userEmail) {
		modalOpen = true;
		modalLoading = true;
		modalSessions = [];
		modalActiveTab = 0;
		modalOurStage = [];
		modalSamsungStage = [];
		modalOurHR = [];
		modalSamsungHR = [];
		modalOurRatios = null;
		modalSamsungRatios = null;
		modalMergedSamsung = null;
		modalSleepwaveData = null;
		modalSamsungCount = 0;
		modalLabel = `${userEmail} / ${date}`;

		try {
			const res = await getApi(
				`/samsung/poc/session-ids?userId=${userId}&date=${date}`,
			);
			if (res?.resultCode === 1000) {
				modalSessions = res.data?.sessions || [];
				if (modalSessions.length > 0) {
					await loadModalCompare(modalSessions[0].id);
				}
			}
		} catch (e) {
			console.error("세션 ID 조회 실패:", e);
		}
		modalLoading = false;
	}

	async function loadModalCompare(sessionId) {
		if (modalCompareCache[sessionId]) {
			applyModalCompare(modalCompareCache[sessionId]);
			return;
		}
		try {
			const res = await getApi(`/samsung/compare/${sessionId}`);
			if (res?.resultCode === 1000) {
				modalCompareCache[sessionId] = res.data;
				applyModalCompare(res.data);
			}
		} catch (e) {
			console.error("세션 비교 데이터 조회 실패:", e);
		}
	}

	function applyModalCompare(data) {
		const sw = data?.sleepwave;
		const samsungList = data?.samsung || [];

		modalSleepwaveData = sw;

		// Sleepwave 수면단계
		if (sw?.rawData?.rawSleepStage && sw?.sleepData?.timeStart) {
			const startTime = parseLocalTimeMs(sw.sleepData.timeStart);
			modalOurStage = parseSleepStage(
				sw.rawData.rawSleepStage,
				startTime,
			);
		} else {
			modalOurStage = [];
		}

		// Samsung 수면단계
		const allStages = samsungList.flatMap((d) => d.stages || []);
		allStages.sort((a, b) => a.startTime - b.startTime);
		modalSamsungStage = parseSamsungStages(allStages);

		// Sleepwave 심박수
		if (sw?.rawData?.rawHeartRate && sw?.sleepData?.timeStart) {
			const startTime = parseLocalTimeMs(sw.sleepData.timeStart);
			modalOurHR = parseHeartRate(sw.rawData.rawHeartRate, startTime);
		} else {
			modalOurHR = [];
		}

		// Samsung 심박수
		const allHR = samsungList.flatMap((d) => d.heartRates || []);
		allHR.sort((a, b) => a.startTime - b.startTime);
		modalSamsungHR = allHR;

		// Sleepwave 비율
		if (sw?.sleepData) {
			const sd = sw.sleepData;
			modalOurRatios = {
				deep: sd.deepSleepRate || 0,
				light: sd.lightSleepRate || 0,
				rem: sd.remSleepRate || 0,
				awake: sd.wasoRate || 0,
			};
		} else {
			modalOurRatios = null;
		}

		// Samsung 비율
		if (allStages.length > 0) {
			const totals = { DEEP: 0, LIGHT: 0, REM: 0, AWAKE: 0 };
			for (const s of allStages) {
				const dur = (s.endTime - s.startTime) / 1000;
				if (totals[s.stage] !== undefined) totals[s.stage] += dur;
			}
			const totalDur = samsungList.reduce(
				(sum, d) => sum + (d.sleep?.duration || 0),
				0,
			);
			modalSamsungRatios = {
				deep: totalDur > 0 ? (totals.DEEP / totalDur) * 100 : 0,
				light: totalDur > 0 ? (totals.LIGHT / totalDur) * 100 : 0,
				rem: totalDur > 0 ? (totals.REM / totalDur) * 100 : 0,
				awake: totalDur > 0 ? (totals.AWAKE / totalDur) * 100 : 0,
			};
		} else {
			modalSamsungRatios = null;
		}

		// Samsung merged
		modalSamsungCount = samsungList.length;
		modalMergedSamsung =
			samsungList.length > 0
				? {
						stages: samsungList.flatMap((d) => d.stages || []),
						sleep: {
							duration: samsungList.reduce(
								(sum, d) => sum + (d.sleep?.duration || 0),
								0,
							),
							startTime: Math.min(
								...samsungList.map(
									(d) => d.sleep?.startTime || Infinity,
								),
							),
						},
					}
				: null;
	}

	async function switchModalTab(idx) {
		modalActiveTab = idx;
		const session = modalSessions[idx];
		if (!session) return;
		await loadModalCompare(session.id);
	}

	function closeModal() {
		modalOpen = false;
		modalCompareCache = {};
	}

	function formatTime(timeStr) {
		if (!timeStr) return "-";
		return timeStr.slice(11, 16); // HH:MM
	}

	// PocUsers 응답 호환 — 평면(S1) / 중첩(S2) / 기존(data.list) 모두 지원
	function extractPocUsers(res) {
		if (!res) return { list: [], cachedAt: "" };
		const d = res.data;
		// S1 평면: { data: [...], cachedAt: "..." }
		if (Array.isArray(d)) {
			return { list: d, cachedAt: res.cachedAt || "" };
		}
		// S2 중첩 (items): { data: { items: [...], cachedAt: "..." } }
		if (d && Array.isArray(d.items)) {
			return { list: d.items, cachedAt: d.cachedAt || res.cachedAt || "" };
		}
		// 기존 + cachedAt: { data: { list: [...], cachedAt: "..." } }
		if (d && Array.isArray(d.list)) {
			return { list: d.list, cachedAt: d.cachedAt || res.cachedAt || "" };
		}
		return { list: [], cachedAt: res.cachedAt || "" };
	}

	function formatCachedAt(s) {
		if (!s) return "";
		const t = new Date(s.replace(" ", "T"));
		if (isNaN(t.getTime())) return `마지막 갱신: ${s}`;
		const hh = String(t.getHours()).padStart(2, "0");
		const mm = String(t.getMinutes()).padStart(2, "0");
		const diffMs = Date.now() - t.getTime();
		const diffMin = Math.max(0, Math.floor(diffMs / 60000));
		let ago;
		if (diffMin < 1) ago = "방금";
		else if (diffMin < 60) ago = `${diffMin}분 전`;
		else ago = `${Math.floor(diffMin / 60)}시간 전`;
		return `마지막 갱신: ${hh}:${mm} (${ago})`;
	}

	async function fetchAll() {
		loading = true;
		const ei = excludeInternal ? "&excludeInternal=true" : "";
		const eiQ = excludeInternal ? "?excludeInternal=true" : "";
		try {
			const [kpiRes, hmRes, usersRes, weeklyHmRes, weeklyRes] =
				await Promise.all([
					getApi(
						`/samsung/poc/overview${excludeInternal ? "?excludeInternal=true" : ""}`,
					),
					getApi(
						`/samsung/poc/heatmap?dateFrom=${dateFrom}&dateTo=${dateTo}${ei}`,
					),
					getApi(`/samsung/poc/users${eiQ}`),
					getApi(`/samsung/poc/weekly-heatmap${eiQ}`),
					getApi(`/samsung/poc/weekly${eiQ}`),
				]);
			if (kpiRes?.resultCode === 1000) kpiData = kpiRes.data;
			if (hmRes?.resultCode === 1000) heatmapData = hmRes.data;
			if (usersRes?.resultCode === 1000) {
				const pu = extractPocUsers(usersRes);
				usersData = pu.list;
				usersCachedAt = pu.cachedAt;
			}
			if (weeklyHmRes?.resultCode === 1000)
				weeklyHeatmapData = weeklyHmRes.data;
			if (weeklyRes?.resultCode === 1000) weeklyData = weeklyRes.data;
		} catch (e) {
			console.error("POC 대시보드 로드 실패:", e);
		}
		loading = false;
	}

	async function handleRefreshUsers() {
		if (usersRefreshing) return;
		usersRefreshing = true;
		try {
			const res = await refreshPocUsers();
			// 429 (락 획득 실패) 또는 비정상 응답 처리
			if (res?.resultCode && res.resultCode !== 1000) {
				const msg = res.message || "잠시 후 다시 시도해주세요.";
				alert(`새로고침이 진행 중입니다. ${msg}`);
				return;
			}
			// refresh 응답에 데이터가 포함되어 있으면 즉시 반영, 아니면 GET 으로 재조회
			const pu = extractPocUsers(res);
			if (pu.list.length > 0 || pu.cachedAt) {
				usersData = pu.list;
				usersCachedAt = pu.cachedAt;
			} else {
				const eiQ = excludeInternal ? "?excludeInternal=true" : "";
				const usersRes = await getApi(`/samsung/poc/users${eiQ}`);
				if (usersRes?.resultCode === 1000) {
					const pu2 = extractPocUsers(usersRes);
					usersData = pu2.list;
					usersCachedAt = pu2.cachedAt;
				}
			}
		} catch (e) {
			console.error("새로고침 실패", e);
			alert("새로고침이 진행 중입니다. 잠시 후 다시 시도해주세요.");
		} finally {
			usersRefreshing = false;
		}
	}

	function handleFilter() {
		fetchAll();
	}

	onMount(async () => {
		menu.set("삼성 헬스 비교");
		menuSub.set("통계 대시보드");
		menuSubSub.set("");
		const { from, to } = getDefaultDates();
		dateFrom = from;
		dateTo = to;
		await Promise.all([fetchAll(), loadBrokerStatus()]);
		connectSSE();
	});

	onDestroy(() => {
		disconnectSSE();
	});
</script>

<div class="container-xxl flex-grow-1 container-p-y">
	<h4 class="fw-bold py-3 mb-4">POC 현황 대시보드</h4>

	<!-- 필터 -->
	<div class="card mb-4">
		<div class="card-body">
			<div class="row g-3 align-items-end">
				<div class="col-md-3">
					<label class="form-label" for="pocDateFrom">시작일</label>
					<input
						id="pocDateFrom"
						type="date"
						class="form-control form-control-sm"
						bind:value={dateFrom}
					/>
				</div>
				<div class="col-md-3">
					<label class="form-label" for="pocDateTo">종료일</label>
					<input
						id="pocDateTo"
						type="date"
						class="form-control form-control-sm"
						bind:value={dateTo}
					/>
				</div>
				<div class="col-md-3 d-flex align-items-end gap-2 pb-1">
					<div class="form-check form-check-sm mb-0">
						<input
							class="form-check-input"
							type="checkbox"
							id="pocExcludeInternal"
							bind:checked={excludeInternal}
						/>
						<label
							class="form-check-label small"
							for="pocExcludeInternal">내부인원 제외</label
						>
					</div>
				</div>
				<div class="col-md-3">
					<button
						class="btn btn-primary btn-sm"
						on:click={handleFilter}
						disabled={loading}
					>
						{loading ? "로딩중..." : "조회"}
					</button>
				</div>
			</div>
		</div>
	</div>

	{#if loading}
		<div class="text-center py-5">
			<div class="spinner-border" role="status"></div>
			<p class="mt-2 text-muted">데이터 로딩중...</p>
		</div>
	{:else}
		<!-- KPI 카드 -->
		{#if kpiData}
			<div class="row g-4 mb-4">
				<div class="col-sm-6 col-xl-3">
					<div class="card kpi-card kpi-indigo">
						<div class="kpi-accent"></div>
						<div class="card-body">
							<div class="kpi-label">총 참여자</div>
							<div class="kpi-value">
								{kpiData.totalUsers}<span class="kpi-denom">
									/ {POC_TOTAL}</span
								>
							</div>
							<div class="kpi-bar">
								<div
									class="kpi-bar-fill"
									style="width:{Math.min(
										(kpiData.totalUsers / POC_TOTAL) * 100,
										100,
									)}%"
								></div>
							</div>
							<div class="kpi-pct">
								{(
									(kpiData.totalUsers / POC_TOTAL) *
									100
								).toFixed(0)}%
							</div>
						</div>
					</div>
				</div>
				<div class="col-sm-6 col-xl-3">
					<div class="card kpi-card kpi-violet">
						<div class="kpi-accent"></div>
						<div class="card-body">
							<div class="kpi-label">전구 연결</div>
							<div class="kpi-value">
								{kpiData.bulbConnected}<span class="kpi-denom">
									/ {POC_TOTAL}</span
								>
							</div>
							<div class="kpi-bar">
								<div
									class="kpi-bar-fill"
									style="width:{Math.min(
										(kpiData.bulbConnected / POC_TOTAL) *
											100,
										100,
									)}%"
								></div>
							</div>
							<div class="kpi-pct">
								{(
									(kpiData.bulbConnected / POC_TOTAL) *
									100
								).toFixed(0)}%
							</div>
						</div>
					</div>
				</div>
				<div class="col-sm-6 col-xl-3">
					<div class="card kpi-card kpi-pink">
						<div class="kpi-accent"></div>
						<div class="card-body">
							<div class="kpi-label">케어 시작</div>
							<div class="kpi-value">
								{kpiData.careStarted}<span class="kpi-denom">
									/ {POC_TOTAL}</span
								>
							</div>
							<div class="kpi-bar">
								<div
									class="kpi-bar-fill"
									style="width:{Math.min(
										(kpiData.careStarted / POC_TOTAL) * 100,
										100,
									)}%"
								></div>
							</div>
							<div class="kpi-pct">
								{(
									(kpiData.careStarted / POC_TOTAL) *
									100
								).toFixed(0)}%
							</div>
						</div>
					</div>
				</div>
				<div class="col-sm-6 col-xl-3">
					<div class="card kpi-card kpi-green">
						<div class="kpi-accent"></div>
						<div class="card-body">
							<div class="kpi-label">
								온라인 전구 <span class="live-dot"></span>
							</div>
							<div class="kpi-value">
								{onlineNow}<span class="kpi-denom">
									/ {POC_TOTAL}</span
								>
							</div>
							<div class="kpi-bar">
								<div
									class="kpi-bar-fill"
									style="width:{Math.min(
										(onlineNow / POC_TOTAL) * 100,
										100,
									)}%"
								></div>
							</div>
							<div class="kpi-pct">
								{((onlineNow / POC_TOTAL) * 100).toFixed(0)}%
							</div>
						</div>
					</div>
				</div>
			</div>
		{/if}

		<!-- 히트맵 -->
		<div class="card mb-4">
			<div
				class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
			>
				<div class="d-flex align-items-center gap-3 flex-wrap">
					<h5 class="card-title mb-0">사용자별 케어 현황</h5>
					<div class="btn-group btn-group-sm" role="group">
						<button
							class="btn {heatmapTimeMode === 'date'
								? 'btn-primary'
								: 'btn-outline-secondary'}"
							on:click={() => (heatmapTimeMode = "date")}
							>날짜별</button
						>
						<button
							class="btn {heatmapTimeMode === 'week'
								? 'btn-primary'
								: 'btn-outline-secondary'}"
							on:click={() => (heatmapTimeMode = "week")}
							>주차별</button
						>
					</div>
					<div class="btn-group btn-group-sm" role="group">
						<button
							class="btn btn-sm {heatmapMode === 'se'
								? 'btn-primary'
								: 'btn-outline-secondary'}"
							on:click={() => (heatmapMode = "se")}
							>SPE 기준</button
						>
						<button
							class="btn btn-sm {heatmapMode === 'care'
								? 'btn-success'
								: 'btn-outline-secondary'}"
							on:click={() => (heatmapMode = "care")}
							>케어시간 기준</button
						>
					</div>
				</div>
				<div class="d-flex align-items-center gap-2 small text-muted">
					<span
						class="legend-dot"
						style="background:#fff;border:1px solid #cbd5e1"
					></span>케어 없음
					<span class="legend-dot" style="background:#f472b6"
					></span>케어 시작일
					{#if heatmapMode === "se"}
						<span>낮은 SPE</span>
						<div class="legend-gradient"></div>
						<span>높은 SPE</span>
					{:else}
						<span class="legend-dot" style="background:#fef3c7"
						></span>0초
						<span>짧은 케어</span>
						<div class="legend-gradient-care"></div>
						<span>긴 케어</span>
					{/if}
				</div>
			</div>
			<div class="card-body">
				{#if heatmapTimeMode === "date"}
					{#if heatmapUsers.length === 0}
						<div class="text-center text-muted py-4">
							데이터가 없습니다
						</div>
					{:else}
						<div class="heatmap-wrap">
							<div
								class="heatmap-grid"
								style="--col-count:{dates.length}"
							>
								<div class="heatmap-label heatmap-label-header">
									<span class="hm-col-id">ID</span>
									<span class="hm-col-name">이름</span>
									<span class="hm-col-psqi">PSQI</span>
								</div>
								{#each dates as d, i}
									<div class="heatmap-date">
										{i % 5 === 0 ? d.slice(5) : ""}
									</div>
								{/each}
								{#each heatmapUsers as user}
									<div
										class="heatmap-label"
										title={user.email}
									>
										<span class="hm-col-id"
											>#{user.userId}</span
										>
										<span class="hm-col-name"
											>{user.email.split("@")[0]}</span
										>
										<span class="hm-col-psqi">
											{#if getPsqi(bulbSerialById[user.userId]) !== null}
												<span
													class="hm-psqi-badge"
													style="color:{psqiColor(
														getPsqi(
															bulbSerialById[
																user.userId
															],
														),
													)}"
													>{getPsqi(
														bulbSerialById[
															user.userId
														],
													)}</span
												>
											{:else}
												<span class="text-muted">-</span
												>
											{/if}
										</span>
									</div>
									{#each user.cells as se, i}
										<!-- svelte-ignore a11y-click-events-have-key-events -->
										<!-- svelte-ignore a11y-no-static-element-interactions -->
										<div
											class="heatmap-cell"
											style={heatmapMode === "se"
												? cellStyle(
														se,
														dates[i],
														user.firstCareDate,
													)
												: careSecsStyle(
														user.cellsCareSecs?.[i],
														dates[i],
														user.firstCareDate,
													)}
											title={heatmapMode === "se"
												? cellTitle(
														se,
														dates[i],
														user.firstCareDate,
													)
												: careSecsTitle(
														user.cellsCareSecs?.[i],
														dates[i],
														user.firstCareDate,
													)}
											on:click={() => {
												const isEmpty = heatmapMode === 'se'
													? (se === null || se === undefined)
													: (user.cellsCareSecs?.[i] === null || user.cellsCareSecs?.[i] === undefined);
												if (!isEmpty) openSessionModal(user.userId, dates[i], user.email);
												}}
										></div>
									{/each}
								{/each}
							</div>
						</div>
					{/if}
				{:else if !weeklyHeatmapData || weeklyHeatmapData.users?.length === 0}
					<div class="text-center text-muted py-4">
						주차별 데이터가 없습니다
					</div>
				{:else}
					{@const maxDay = weeklyHeatmapData.maxDay}
					<div class="heatmap-wrap">
						<div class="heatmap-grid" style="--col-count:{maxDay}">
							<!-- X축 헤더: 7일마다 주차 표시, 나머지 빈칸 -->
							<div class="heatmap-label heatmap-label-header">
								<span class="hm-col-id">ID</span>
								<span class="hm-col-name">이름</span>
								<span class="hm-col-psqi">PSQI</span>
							</div>
							{#each Array.from({ length: maxDay }, (_, i) => i) as dayIdx}
								<div class="heatmap-date">
									{dayIdx % 7 === 0
										? `${Math.floor(dayIdx / 7) + 1}W`
										: ""}
								</div>
							{/each}
							<!-- 사용자 행 -->
							{#each weeklyHeatmapData.users as user}
								<div class="heatmap-label" title={user.email}>
									<span class="hm-col-id">#{user.userId}</span
									>
									<span class="hm-col-name"
										>{user.email.split("@")[0]}</span
									>
									<span class="hm-col-psqi">
										{#if getPsqi(bulbSerialById[user.userId]) !== null}
											<span
												class="hm-psqi-badge"
												style="color:{psqiColor(
													getPsqi(
														bulbSerialById[
															user.userId
														],
													),
												)}"
												>{getPsqi(
													bulbSerialById[user.userId],
												)}</span
											>
										{:else}
											<span class="text-muted">-</span>
										{/if}
									</span>
								</div>
								{#each Array.from({ length: maxDay }, (_, i) => i) as dayIdx}
									<!-- svelte-ignore a11y-click-events-have-key-events -->
									<!-- svelte-ignore a11y-no-static-element-interactions -->
									<div
										class="heatmap-cell"
										style={heatmapMode === "se"
											? weekCellStyle(
													user.cells?.[dayIdx],
												)
											: weekCareStyle(
													user.cellsCareSecs?.[
														dayIdx
													],
												)}
										title={heatmapMode === "se"
											? weekCellTitle(
													user.cells?.[dayIdx],
													dayIdx + 1,
													user.firstCareDate,
												)
											: weekCareTitle(
													user.cellsCareSecs?.[
														dayIdx
													],
													dayIdx + 1,
													user.firstCareDate,
												)}
										on:click={() => {
											const actualDate =
												user.firstCareDate
													? addDaysToDate(
															user.firstCareDate,
															dayIdx,
														)
													: null;
											const isEmpty = heatmapMode === 'se'
						? (user.cells?.[dayIdx] === null || user.cells?.[dayIdx] === undefined)
						: (user.cellsCareSecs?.[dayIdx] === null || user.cellsCareSecs?.[dayIdx] === undefined);
					if (actualDate && !isEmpty)
												openSessionModal(user.userId, actualDate, user.email);
										}}
									></div>
								{/each}
							{/each}
						</div>
					</div>

					{#if weeklyData && weeklyData.weeks?.length > 0}
						<div class="mt-4">
							<h6 class="text-muted small fw-bold mb-2">
								주차별 집계 요약
							</h6>
							<div class="table-responsive">
								<table
									class="table table-sm table-bordered table-hover"
								>
									<thead class="table-light">
										<tr>
											<th>주차</th>
											<th>케어 인원수</th>
											<th>평균 입면시간</th>
											<th>평균 Samsung SPE</th>
											<th>평균 Samsung SE</th>
											<th>평균 Samsung TST</th>
											<th>평균 Samsung WASO</th>
										</tr>
									</thead>
									<tbody>
										{#each weeklyData.weeks as w, i}
											<tr>
												<td class="text-center fw-bold">{w}주차</td>
												<td class="text-center">{weeklyData.careUsers[i]}명</td>
												<td class="text-center">{weeklyData.avgSleepLatency[i] ? Math.round(weeklyData.avgSleepLatency[i] / 60) + '분' : '-'}</td>
												<td class="text-center">{weeklyData.avgSpe?.[i] ? weeklyData.avgSpe[i].toFixed(1) + '%' : '-'}</td>
												<td class="text-center">{weeklyData.avgSamsungSE?.[i] ? weeklyData.avgSamsungSE[i].toFixed(1) + '%' : '-'}</td>
												<td class="text-center">{weeklyData.avgSamsungTst[i] ? (weeklyData.avgSamsungTst[i] / 3600).toFixed(1) + 'h' : '-'}</td>
												<td class="text-center">{weeklyData.avgSamsungWaso[i] ? Math.round(weeklyData.avgSamsungWaso[i] / 60) + '분' : '-'}</td>
											</tr>
										{/each}
									</tbody>
								</table>
							</div>
						</div>
					{/if}
				{/if}
			</div>
		</div>

		<!-- 사용자 요약 테이블 -->
		<div class="card">
			<div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
				<h5 class="card-title mb-0">사용자별 요약</h5>
				<div class="cache-meta d-flex align-items-center gap-2">
					{#if usersCachedAt}
						<span class="text-muted small">{formatCachedAt(usersCachedAt)}</span>
					{/if}
					<button
						type="button"
						class="btn btn-sm btn-outline-secondary"
						on:click={handleRefreshUsers}
						disabled={usersRefreshing}
						title="서버 캐시를 무효화하고 즉시 재계산합니다"
					>
						{usersRefreshing ? "갱신 중..." : "🔄 새로고침"}
					</button>
				</div>
			</div>
			<div class="table-responsive">
				<table class="table table-sm table-hover table-bordered">
					<thead class="table-light">
						<tr>
							<th>ID</th>
							<th>이메일</th>
							<th>가입일</th>
							<th>전구 시리얼</th>
							<th>PSQI</th>
							<th>케어 시작일</th>
							<th>총 세션</th>
							<th>Samsung TST</th>
							<th>SPE</th>
							<th>Samsung SE</th>
							<th>온라인</th>
						</tr>
					</thead>
					<tbody>
						{#if usersData.length === 0}
							<tr>
								<td
									colspan="11"
									class="text-center text-muted py-4"
									>데이터가 없습니다</td
								>
							</tr>
						{:else}
							{#each usersData as u}
								<tr>
									<td class="text-center text-muted small"
										>#{u.userId}</td
									>
									<td>{u.email || "-"}</td>
									<td>{u.joinedAt || "-"}</td>
									<td
										><code class="small"
											>{u.firstBulbSerial || "-"}</code
										></td
									>
									<td class="text-center">
										{#if getPsqi(u.firstBulbSerial) !== null}
											<span
												class="psqi-badge"
												style="color:{psqiColor(
													getPsqi(u.firstBulbSerial),
												)}"
											>
												{getPsqi(u.firstBulbSerial)}
											</span>
										{:else}
											-
										{/if}
									</td>
									<td>{u.firstCareDate || "-"}</td>
									<td class="text-center"
										>{u.totalSessions ?? 0}</td
									>
									<td class="text-center"
										>{secsToHMS(u.avgSamsungTst)}</td
									>
									<td class="text-center">
										{#if u.avgSpe !== null && u.avgSpe !== undefined && u.avgSpe > 0}
											<span
												class="se-badge"
												style="color:{seColor(
													u.avgSpe,
												)}"
											>
												{u.avgSpe.toFixed(1)}%
											</span>
										{:else}
											-
										{/if}
									</td>
									<td class="text-center">
										{#if u.avgSamsungSE > 0}
											<span
												class="se-badge"
												style="color:{seColor(
													u.avgSamsungSE,
												)}"
											>
												{u.avgSamsungSE.toFixed(1)}%
											</span>
										{:else}
											-
										{/if}
									</td>
									<td class="text-center">
										<span
											style="color:{$mqttDevices[
												'tasmota_' + u.userId
											]?.online === 'true'
												? '#10b981'
												: '#ef4444'};font-size:14px"
											>●</span
										>
									</td>
								</tr>
							{/each}
						{/if}
					</tbody>
				</table>
			</div>
			{#if usersData.length > 0}
				<div class="card-footer text-muted small">
					{usersData.length}명 표시{excludeInternal
						? " (내부인원 제외)"
						: ""}
				</div>
			{/if}
		</div>
	{/if}
</div>

<!-- 세션 상세 모달 -->
{#if modalOpen}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div class="modal-backdrop fade show" on:click={closeModal}></div>
	<div class="modal fade show d-block" tabindex="-1" style="overflow-y:auto">
		<div class="modal-dialog modal-xl modal-dialog-scrollable">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<i class="bx bx-moon me-1"></i>수면 세션 상세
						<span class="text-muted fs-6 ms-2">{modalLabel}</span>
					</h5>
					<button
						type="button"
						class="btn-close"
						on:click={closeModal}
					></button>
				</div>
				<div class="modal-body">
					{#if modalLoading}
						<div class="text-center py-5">
							<div class="spinner-border" role="status"></div>
							<p class="mt-2 text-muted">데이터 로딩중...</p>
						</div>
					{:else if modalSessions.length === 0}
						<div class="alert alert-info">
							<i class="bx bx-info-circle me-1"></i>해당 날짜에
							케어 세션이 없습니다.
						</div>
					{:else}
						{#if modalSessions.length > 1}
							<ul class="nav nav-tabs mb-3">
								{#each modalSessions as sess, idx}
									<li class="nav-item">
										<!-- svelte-ignore a11y-missing-attribute -->
										<!-- svelte-ignore a11y-click-events-have-key-events -->
										<!-- svelte-ignore a11y-no-static-element-interactions -->
										<a
											class="nav-link {modalActiveTab ===
											idx
												? 'active'
												: ''}"
											on:click={() => switchModalTab(idx)}
										>
											세션 {idx + 1}
											<small class="text-muted ms-1">
												{formatTime(
													sess.timeStart,
												)}~{formatTime(sess.timeEnd)}
											</small>
										</a>
									</li>
								{/each}
							</ul>
						{/if}

						{#if !modalSleepwaveData}
							<div class="text-center py-4 text-muted">
								세션 데이터를 불러오는 중...
							</div>
						{:else}
							<SessionInfoCard
								sleepData={modalSleepwaveData?.sleepData}
								samsungCount={modalSamsungCount}
							/>
							<ComparisonCard
								ourData={modalSleepwaveData}
								samsungData={modalMergedSamsung}
							/>
							<SleepStageChart
								ourData={modalOurStage}
								samsungData={modalSamsungStage}
							/>
							<HeartRateChart
								ourData={modalOurHR}
								samsungData={modalSamsungHR}
							/>
							<StageRatioChart
								ourRatios={modalOurRatios}
								samsungRatios={modalSamsungRatios}
							/>
						{/if}
					{/if}
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	/* ── KPI Cards ──────────────────────────────────── */
	.kpi-card {
		border: 1px solid #e2e8f0;
		border-radius: 10px;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
		background: #fff;
		overflow: hidden;
	}
	.kpi-accent {
		height: 4px;
		border-radius: 0;
	}
	.kpi-indigo .kpi-accent {
		background: #6366f1;
	}
	.kpi-violet .kpi-accent {
		background: #8b5cf6;
	}
	.kpi-pink .kpi-accent {
		background: #ec4899;
	}
	.kpi-green .kpi-accent {
		background: #10b981;
	}

	.kpi-label {
		font-size: 0.75rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.6px;
		color: #94a3b8;
		margin-bottom: 4px;
	}
	.kpi-value {
		font-size: 2rem;
		font-weight: 800;
		line-height: 1.1;
		letter-spacing: -0.5px;
	}
	.kpi-indigo .kpi-value {
		color: #6366f1;
	}
	.kpi-violet .kpi-value {
		color: #8b5cf6;
	}
	.kpi-pink .kpi-value {
		color: #ec4899;
	}
	.kpi-green .kpi-value {
		color: #10b981;
	}

	.kpi-denom {
		font-size: 1rem;
		font-weight: 500;
		color: #cbd5e1;
	}
	.kpi-bar {
		height: 5px;
		background: #f1f5f9;
		border-radius: 3px;
		margin-top: 12px;
	}
	.kpi-bar-fill {
		height: 100%;
		border-radius: 3px;
		transition: width 0.5s ease;
	}
	.kpi-indigo .kpi-bar-fill {
		background: #6366f1;
	}
	.kpi-violet .kpi-bar-fill {
		background: #8b5cf6;
	}
	.kpi-pink .kpi-bar-fill {
		background: #ec4899;
	}
	.kpi-green .kpi-bar-fill {
		background: #10b981;
	}
	.kpi-pct {
		font-size: 0.78rem;
		color: #94a3b8;
		margin-top: 5px;
	}

	/* Live 애니메이션 */
	.live-dot {
		display: inline-block;
		width: 7px;
		height: 7px;
		background: #10b981;
		border-radius: 50%;
		margin-left: 4px;
		vertical-align: middle;
		animation: livepulse 1.6s ease-in-out infinite;
	}
	@keyframes livepulse {
		0%,
		100% {
			opacity: 1;
			transform: scale(1);
		}
		50% {
			opacity: 0.35;
			transform: scale(0.8);
		}
	}

	/* ── Heatmap ─────────────────────────────────────── */
	.heatmap-wrap {
		overflow-x: auto;
		overflow-y: auto;
		max-height: 520px;
		padding-bottom: 4px;
	}
	.heatmap-grid {
		display: grid;
		grid-template-columns: 150px repeat(var(--col-count), minmax(12px, 1fr));
		gap: 3px;
	}
	.heatmap-label {
		display: grid;
		grid-template-columns: 32px 1fr 28px;
		gap: 2px;
		font-size: 11px;
		color: #64748b;
		align-items: center;
		padding-right: 8px;
		height: 18px;
		line-height: 18px;
	}
	.heatmap-label-header {
		font-size: 9px;
		font-weight: 700;
		color: #94a3b8;
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}
	.hm-col-id {
		text-align: center;
		font-size: 10px;
		color: #94a3b8;
		font-weight: 600;
	}
	.hm-col-name {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.hm-col-psqi {
		text-align: center;
	}
	.hm-psqi-badge {
		font-size: 9px;
		font-weight: 700;
		opacity: 0.85;
	}
	.heatmap-date {
		font-size: 10px;
		color: #94a3b8;
		text-align: center;
		height: 16px;
		line-height: 16px;
	}
	.heatmap-cell {
		height: 18px;
		border-radius: 3px;
		cursor: pointer;
		transition: filter 0.1s;
	}
	.heatmap-cell:hover {
		filter: brightness(1.15);
	}

	/* ── Legend ──────────────────────────────────────── */
	.legend-dot {
		display: inline-block;
		width: 12px;
		height: 12px;
		border-radius: 2px;
		vertical-align: middle;
	}
	.legend-gradient {
		display: inline-block;
		width: 50px;
		height: 10px;
		background: linear-gradient(
			90deg,
			rgba(99, 102, 241, 0.15),
			rgba(99, 102, 241, 1)
		);
		border-radius: 2px;
		vertical-align: middle;
	}
	.legend-gradient-care {
		display: inline-block;
		width: 50px;
		height: 10px;
		background: linear-gradient(
			90deg,
			rgba(16, 185, 129, 0.15),
			rgba(16, 185, 129, 1)
		);
		border-radius: 2px;
		vertical-align: middle;
	}

	/* ── Indigo Button ──────────────────────────────── */
	:global(.btn-indigo) {
		background-color: #6366f1;
		border-color: #6366f1;
		color: #fff;
	}
	:global(.btn-indigo:hover) {
		background-color: #4f46e5;
		border-color: #4f46e5;
		color: #fff;
	}

	/* ── Table ───────────────────────────────────────── */
	:global(.table thead th) {
		text-align: center !important;
		vertical-align: middle !important;
		font-size: 0.78rem;
		letter-spacing: 0.3px;
	}
	:global(tbody tr:hover) {
		background: rgba(99, 102, 241, 0.04) !important;
	}

	.se-badge {
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: 600;
		font-size: 12px;
		background: rgba(99, 102, 241, 0.12);
	}
	.psqi-badge {
		display: inline-block;
		padding: 2px 8px;
		border-radius: 10px;
		font-weight: 600;
		font-size: 12px;
		background: rgba(0, 0, 0, 0.06);
	}
</style>
